import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { manualOps, type ManualOperationEntry } from '@/api/documents';
import { orgs } from '@/api/catalogs';
import AccountSelect from '@/components/AccountSelect';
import { useConfirm } from '@/components/ConfirmProvider';
import DatePopover from '@/components/DatePopover';
import ProjectSelect from '@/components/ProjectSelect';
import { useNumberPreview } from '@/hooks/useNumberPreview';
import { usePermissionsByOrg } from '@/hooks/usePermissionsByOrg';
import FopExclusionRescueSection from '@/components/FopExclusionRescueSection';
import DocumentToolbar from '@/shared/components/DocumentToolbar';

interface FormState {
  organization_id: string;
  document_number: string;
  document_date: string;
  operation_content: string;
  comment: string;
  project_id: string | null;
  entries: ManualOperationEntry[];
  // Фаза 2.1 #3: subtype для специфічних flows.
  operation_subtype: '' | 'batch_correction';
  related_batch_key_id: string;
  new_cost_price: string;
}

const EMPTY_FORM: FormState = {
  organization_id: '',
  document_number: '',
  document_date: new Date().toISOString().slice(0, 16),
  operation_content: '',
  comment: '',
  project_id: null,
  entries: [{ line_number: 1, dt_account_id: '', cr_account_id: '', amount: '0', content: '' }],
  operation_subtype: '',
  related_batch_key_id: '',
  new_cost_price: '',
};

export default function ManualOperationEditor() {
  const confirm = useConfirm();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const { id } = useParams();
  const isNew = !id || id === 'new';

  const orgList = useQuery({ queryKey: ['orgs'], queryFn: () => orgs.list({ limit: 200 }) });
  // UX β: фільтр org dropdown по accounting.manual.manage permission
  const {
    availableOrgs,
    hasPermissionInAnyOrg,
    totalAccessibleOrgs,
    loading: permsLoading,
  } = usePermissionsByOrg('accounting.manual.manage');
  const docQuery = useQuery({
    queryKey: ['manual-op', id],
    queryFn: () => manualOps.get(id!),
    enabled: !isNew,
  });

  const [form, setForm] = useState<FormState>(EMPTY_FORM);
  const numberPreview = useNumberPreview('doc.manual_operations', form.organization_id, isNew);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (isNew) {
      const firstOrg = availableOrgs[0]?.id ?? '';
      setForm((f) => f.organization_id ? f : { ...f, organization_id: firstOrg });
    } else if (docQuery.data) {
      const d = docQuery.data;
      setForm({
        organization_id: d.organization_id,
        document_number: d.document_number,
        document_date: d.document_date.slice(0, 16),
        operation_content: d.operation_content ?? '',
        comment: d.comment ?? '',
        project_id: d.project_id ?? null,
        entries: d.entries.length
          ? d.entries.map((e) => ({
              line_number: e.line_number,
              dt_account_id: e.dt_account_id,
              cr_account_id: e.cr_account_id,
              amount: String(e.amount),
              content: e.content ?? '',
            }))
          : EMPTY_FORM.entries,
        operation_subtype: ((d as { operation_subtype?: string }).operation_subtype as 'batch_correction' | '' | undefined) ?? '',
        related_batch_key_id: (d as { related_batch_key_id?: string }).related_batch_key_id ?? '',
        new_cost_price: (d as { new_cost_price?: string }).new_cost_price ?? '',
      });
    }
  }, [isNew, docQuery.data, availableOrgs]);

  const isPosted = !isNew && (docQuery.data?.posted ?? false);
  const totalAmount = useMemo(
    () => form.entries.reduce((s, e) => s + Number(e.amount || 0), 0),
    [form.entries],
  );

  const create = useMutation({
    mutationFn: () => manualOps.create({
      organization_id: form.organization_id,
      document_number: form.document_number,
      document_date: new Date(form.document_date).toISOString(),
      operation_content: form.operation_content || null,
      comment: form.comment || null,
      project_id: form.project_id,
      entries: form.entries,
      // Фаза 2.1 #3: subtype + batch correction поля.
      operation_subtype: form.operation_subtype || null,
      related_batch_key_id: form.related_batch_key_id || null,
      new_cost_price: form.new_cost_price || null,
    } as unknown as Parameters<typeof manualOps.create>[0]),
    onSuccess: (doc) => {
      qc.invalidateQueries({ queryKey: ['manual-ops'] });
      navigate(`/manual-operations/${doc.id}`);
    },
    onError: (e) => setError(extractError(e)),
  });
  const update = useMutation({
    mutationFn: () => manualOps.update(id!, {
      document_number: form.document_number,
      document_date: new Date(form.document_date).toISOString(),
      operation_content: form.operation_content || null,
      comment: form.comment || null,
      project_id: form.project_id,
      entries: form.entries,
    }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['manual-op', id] }),
    onError: (e) => setError(extractError(e)),
  });
  const post = useMutation({
    mutationFn: () => manualOps.post(id!),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['manual-op', id] });
      qc.invalidateQueries({ queryKey: ['manual-ops'] });
    },
    onError: (e) => setError(extractError(e)),
  });
  const unpost = useMutation({
    mutationFn: () => manualOps.unpost(id!),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['manual-op', id] });
      qc.invalidateQueries({ queryKey: ['manual-ops'] });
    },
    onError: (e) => setError(extractError(e)),
  });
  const remove = useMutation({
    mutationFn: () => manualOps.remove(id!),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['manual-ops'] });
      navigate('/manual-operations');
    },
  });

  function setEntry(i: number, patch: Partial<ManualOperationEntry>) {
    setForm((f) => ({
      ...f,
      entries: f.entries.map((e, idx) => idx === i ? { ...e, ...patch } : e),
    }));
  }
  function addRow() {
    setForm((f) => ({
      ...f,
      entries: [...f.entries, {
        line_number: f.entries.length + 1,
        dt_account_id: '', cr_account_id: '', amount: '0', content: '',
      }],
    }));
  }
  function removeRow(i: number) {
    setForm((f) => ({
      ...f,
      entries: f.entries
        .filter((_, idx) => idx !== i)
        .map((e, idx) => ({ ...e, line_number: idx + 1 })),
    }));
  }

  // UX β: warning якщо новий документ а user не має accounting.manual.manage у жодній org
  if (isNew && !permsLoading && !hasPermissionInAnyOrg) {
    return (
      <div className="card">
        <div style={{ padding: '1.5rem', textAlign: 'center' }}>
          <div style={{
            padding: '1rem', background: '#fef3c7', borderRadius: 6,
            border: '1px solid #fbbf24', color: '#92400e',
            maxWidth: 600, margin: '1rem auto',
          }}>
            ⚠ У вас немає прав створювати ручну операцію у жодній з ваших
            організацій ({totalAccessibleOrgs}). Зверніться до
            адміністратора tenant'а.
          </div>
          <button onClick={() => navigate('/manual-operations')}>Скасувати</button>
        </div>
      </div>
    );
  }

  return (
    <div className="card">
      <div className="toolbar">
        <h2 style={{ margin: 0 }}>
          {isNew ? t('mo.new') : `${t('mo.title')}: ${form.document_number}`}
          {isPosted && <span style={{ marginLeft: '0.75rem', color: '#15803d', fontSize: '1rem' }}>✓ {t('mo.posted_yes')}</span>}
        </h2>
        <DocumentToolbar
          onBackToList={() => navigate('/manual-operations')}
          onSave={() => isNew ? create.mutate() : update.mutate()}
          saveDisabled={create.isPending || update.isPending}
          isPosted={isPosted}
          isNew={isNew}
          onPost={() => post.mutate()}
          postDisabled={!form.entries.some((e) => e.dt_account_id && e.cr_account_id)}
          postPending={post.isPending}
          onUnpost={() => unpost.mutate()}
          unpostPending={unpost.isPending}
          onDelete={() => { confirm(t('common.confirm_delete')).then((ok) => { if (ok) remove.mutate(); }) }}
          deletePending={remove.isPending}
        />
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '0.75rem' }}>
        <div className="field">
          <label>{t('mo.organization')}</label>
          <select
            value={form.organization_id}
            onChange={(e) => setForm({ ...form, organization_id: e.target.value })}
            disabled={!isNew}
          >
            <option value="">—</option>
            {(isNew ? availableOrgs : orgList.data?.items ?? []).map((o) => (
              <option key={o.id} value={o.id}>{o.code} — {o.name}</option>
            ))}
          </select>
          {isNew && availableOrgs.length < totalAccessibleOrgs && (
            <small style={{ color: '#64748b', fontSize: '0.75rem' }}>
              Показано {availableOrgs.length} з {totalAccessibleOrgs} ваших організацій
              (інші не дозволяють цю операцію)
            </small>
          )}
        </div>
        <div className="field">
          <label>{t('mo.number')}</label>
          <input
            value={form.document_number}
            onChange={(e) => setForm({ ...form, document_number: e.target.value })}
                 placeholder={isNew && numberPreview ? `буде: ${numberPreview}` : ''}
            disabled={isPosted}
          />
        </div>
        <div className="field">
          <label>{t('mo.date')}</label>
          <DatePopover
            withTime
            value={form.document_date}
            onChange={(v) => setForm({ ...form, document_date: v })}
            disabled={isPosted}
          />
        </div>
      </div>
      <div className="field">
        <label>{t('mo.content')}</label>
        <input
          value={form.operation_content}
          onChange={(e) => setForm({ ...form, operation_content: e.target.value })}
          disabled={isPosted}
        />
      </div>
      <div className="field">
        <label>Проект / напрям</label>
        <ProjectSelect
          value={form.project_id}
          organizationId={form.organization_id}
          onChange={(v) => setForm({ ...form, project_id: v })}
          disabled={isPosted} />
      </div>


      {/* Фаза 2.1 #3: батч-correction subform */}
      <div style={{
        marginTop: '1rem', padding: '0.75rem',
        background: '#fef3c7', borderRadius: 6,
      }}>
        <label>
          <input
            type="checkbox"
            checked={form.operation_subtype === 'batch_correction'}
            disabled={isPosted || !isNew}
            onChange={(e) => setForm({
              ...form,
              operation_subtype: e.target.checked ? 'batch_correction' : '',
              related_batch_key_id: e.target.checked ? form.related_batch_key_id : '',
              new_cost_price: e.target.checked ? form.new_cost_price : '',
            })}
          />
          {' '}<strong>Коригування партії (batch_correction)</strong>
        </label>
        {form.operation_subtype === 'batch_correction' && (
          <div style={{ marginTop: '0.5rem', display: 'grid',
                        gridTemplateColumns: '1fr 1fr', gap: '0.5rem' }}>
            <div className="field">
              <label>ID партії (reg.batch_keys.id)</label>
              <input
                value={form.related_batch_key_id}
                disabled={isPosted}
                onChange={(e) => setForm({ ...form, related_batch_key_id: e.target.value })}
                placeholder="UUID партії"
              />
            </div>
            <div className="field">
              <label>Нова собівартість (cost_price)</label>
              <input
                type="number" step="0.0001"
                value={form.new_cost_price}
                disabled={isPosted}
                onChange={(e) => setForm({ ...form, new_cost_price: e.target.value })}
              />
            </div>
            <div style={{ gridColumn: 'span 2', fontSize: '0.85rem', color: '#92400e' }}>
              ⚠ При проведенні: UPDATE batch_keys + FORWARD recalc movements
              від document_date + acc.entries з різницею (Дт 281 — Кт 281
              на subconto партії). Entries поля не використовуються.
            </div>
          </div>
        )}
      </div>

      <h3 style={{ marginTop: '1.5rem' }}>{t('mo.entries')}</h3>
      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.9rem' }}>
        <thead>
          <tr style={{ background: '#f8fafc', textAlign: 'left' }}>
            <th style={{ padding: '0.5rem', width: 50 }}>#</th>
            <th style={{ padding: '0.5rem' }}>{t('mo.dt')}</th>
            <th style={{ padding: '0.5rem' }}>{t('mo.cr')}</th>
            <th style={{ padding: '0.5rem', width: 140 }}>{t('mo.amount')}</th>
            <th style={{ padding: '0.5rem' }}>{t('mo.entry_content')}</th>
            <th style={{ padding: '0.5rem', width: 50 }}></th>
          </tr>
        </thead>
        <tbody>
          {form.entries.map((e, i) => (
            <tr key={i} style={{ borderTop: '1px solid #e2e8f0' }}>
              <td style={{ padding: '0.5rem' }}>{e.line_number}</td>
              <td style={{ padding: '0.5rem' }}>
                <AccountSelect
                  value={e.dt_account_id}
                  onChange={(v) => setEntry(i, { dt_account_id: v })}
                />
              </td>
              <td style={{ padding: '0.5rem' }}>
                <AccountSelect
                  value={e.cr_account_id}
                  onChange={(v) => setEntry(i, { cr_account_id: v })}
                />
              </td>
              <td style={{ padding: '0.5rem' }}>
                <input
                  type="number"
                  step="0.01"
                  value={e.amount}
                  onChange={(ev) => setEntry(i, { amount: ev.target.value })}
                  disabled={isPosted}
                />
              </td>
              <td style={{ padding: '0.5rem' }}>
                <input
                  value={e.content ?? ''}
                  onChange={(ev) => setEntry(i, { content: ev.target.value })}
                  disabled={isPosted}
                />
              </td>
              <td style={{ padding: '0.5rem' }}>
                {!isPosted && form.entries.length > 1 && (
                  <button onClick={() => removeRow(i)} className="danger" style={{ padding: '0.3rem 0.6rem' }}>×</button>
                )}
              </td>
            </tr>
          ))}
        </tbody>
        <tfoot>
          <tr style={{ borderTop: '2px solid #cbd5e1', fontWeight: 600 }}>
            <td colSpan={3} style={{ padding: '0.5rem', textAlign: 'right' }}>{t('mo.total')}:</td>
            <td style={{ padding: '0.5rem' }}>{totalAmount.toFixed(2)}</td>
            <td colSpan={2}></td>
          </tr>
        </tfoot>
      </table>
      {!isPosted && (
        <button onClick={addRow} style={{ marginTop: '0.75rem' }}>+ {t('mo.add_row')}</button>
      )}
      {error && <div className="error">{error}</div>}
      {isPosted && !isNew && (
        <FopExclusionRescueSection
          organizationId={form.organization_id}
          documentId={id!}
        />
      )}
    </div>
  );
}

function extractError(e: unknown): string {
  if (typeof e === 'object' && e && 'response' in e) {
    const r = (e as { response?: { data?: { detail?: string } } }).response;
    return r?.data?.detail ?? 'Error';
  }
  return String(e);
}
